import { t } from 'i18next';
import JSZip from 'jszip';
import { BpFile } from '../hooks/useFiles';

const mimeTypes: { [key: string]: string } = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/bmp': 'BMP',
  'image/svg+xml': 'SVG',
  'application/json': 'JSON',
  'text/plain': 'Text',
  'text/javascript': 'JavaScript',
  'text/csv': 'CSV',
  'text/html': 'HTML',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.oasis.opendocument.spreadsheet': 'Tabelle',
  'application/vnd.oasis.opendocument.presentation': 'Präsentation',
  'application/x-yaml': 'YAML',
  'application/pdf': 'PDF',
  'application/gzip': 'GZIP',
  'audio/mpeg': 'MP3',
  'video/mp4': 'MP4',
  'video/mpeg': 'MPEG',
  'video/x-msvideo': 'Video',
};

export function getMimeType(mediaType: string): string {
  return mimeTypes[mediaType] ?? t('files.title', { count: 1 });
}

export function formatBytes(bytes: number, decimals = 0): string {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

export function getFilenameWithoutExtension(filename: string): string {
  const endIndex = filename.lastIndexOf('.');
  return endIndex === -1 ? filename : filename.substring(0, endIndex);
}

export function getFileExtension(filename: string): string {
  const endIndex = filename.lastIndexOf('.');
  return endIndex === -1 ? '' : filename.substring(endIndex + 1);
}

export function getFileUrl(uuid: string, token: string): string {
  return `${import.meta.env.VITE_APP_BACKEND_URI}${
    import.meta.env.VITE_APP_BACKEND_REST_PATH
  }/files/${uuid}?token=${token}`;
}

export function handleFileDownload(file: BpFile): void {
  const token = file.downloadToken;
  const link = document.createElement('a');
  link.download = file.filename ?? '';
  link.href = getFileUrl(file.uuid, token ?? '');
  link.target = '_blank';
  link.click();
}

export async function createZipFile(files: BpFile[]): Promise<Blob> {
  const zip = new JSZip();

  await Promise.all(
    files.map(async (file) => {
      try {
        const url = getFileUrl(file.uuid, file.downloadToken);
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to fetch ${file.filename}`);
        }

        const blob = await response.blob();
        zip.file(file.filename, blob);
      } catch (error) {
        console.error(`Error fetching file ${file.filename}:`, error);
      }
    }),
  );

  return await zip.generateAsync({ type: 'blob' });
}

export async function downloadZipFile(files: BpFile[], name: string = 'files'): Promise<void> {
  const zip = await createZipFile(files);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(zip);
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
}
